'use client';

import { type ComponentProps } from 'react';
import { Root, Item, Trigger, Header, Content } from '@radix-ui/react-accordion';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from '@/util/utils';

function Accordion({ ...props }: ComponentProps<typeof Root>) {
    return <Root data-slot="accordion" {...props} />;
}

function AccordionItem({
    className,
    disabled,
    value,
    ref,
    asChild,
    ...props
}: ComponentProps<typeof Item>) {
    return (
        <Item
            data-slot="accordion-item"
            className={cn('border-border border-b', className)}
            disabled={disabled}
            value={value}
            asChild={asChild}
            ref={ref}
            {...props}
        />
    );
}

function AccordionTrigger({
    className,
    children,
    ref,
    asChild,
    ...props
}: ComponentProps<typeof Trigger>) {
    return (
        <Header className="flex">
            <Trigger
                data-slot="accordion-trigger"
                className={cn(
                    'focus-visible:ring-outline text-foreground flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-60 [&[data-state=open]>svg]:rotate-180',
                    className,
                )}
                asChild={asChild}
                ref={ref}
                {...props}
            >
                {children}
                <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
            </Trigger>
        </Header>
    );
}

function AccordionContent({
    className,
    children,
    forceMount,
    ref,
    asChild,
    ...props
}: ComponentProps<typeof Content>) {
    return (
        <Content
            data-slot="accordion-content"
            className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down text-foreground overflow-hidden text-sm font-normal"
            forceMount={forceMount}
            asChild={asChild}
            ref={ref}
            {...props}
        >
            <div className={cn('pt-0 pb-4', className)}>{children}</div>
        </Content>
    );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
